/**
 * Viewport stencil clipping (PR-D).
 *
 * AutoCAD's paper-space VIEWPORT entity windows model-space content
 * inside a paper-space rectangle, optionally rotated, and optionally
 * clipped by an arbitrary XCLIP polygon. The 3D renderer implements
 * this through Three.js's stencil-buffer test:
 *
 *   1. **Stencil pass.** Draw the (optionally rotated, optionally
 *      polygonal) viewport boundary into the stencil buffer, writing
 *      a unique `viewportIdx` into every pixel covered by the
 *      polygon. Geometry is emitted as a triangle list via earcut so
 *      arbitrary polygon shapes are supported (rectangles are the
 *      4-vertex special case).
 *   2. **Children render pass.** Set `material.stencilFunc = EQUAL`,
 *      `stencilRef = viewportIdx` on every child material (or use a
 *      shared material whose stencil settings are kept in sync via
 *      `prepareChildMaterial`). Three.js's stencil test then
 *      discards every fragment outside the polygon.
 *
 * # Why stencil over scissor
 *
 * `gl.scissor()` accepts an axis-aligned rectangle — no rotation, no
 * polygon. Stencil pixels are arbitrary, so the same code path
 * handles rotated viewports (Q12) and XCLIP polygons (edge case 30
 * in the plan) without branching.
 *
 * # `viewportIdx` collisions
 *
 * The stencil buffer is 8 bits → 255 distinct viewport ids. Real
 * paper-space layouts ship < 20 viewports, so we never run out.
 * `assignViewportIdx` returns the next free id starting at 1
 * (0 is reserved for "no viewport" — children outside any clipping
 * scope render unconditionally).
 *
 * # Edge cases (CLAUDE.md PR-D §8 + edge case 30)
 *
 *   - 0-area clip → `tessellateViewportClip` returns an empty triangle
 *     list. Renderer should skip the stencil pass and render children
 *     unclipped (full visibility — falls back to "no clipping").
 *   - clipPath with < 3 vertices → fall back to rectangular rect.
 *   - Rotation is already baked into the rect by the adapter
 *     (`viewportToRenderable.ts`); this module does NOT re-rotate.
 */

import * as THREE from "three";

import type { Vec3 } from "../types";
import { applyOffset } from "../coordinateOrigin";
import { tessellateHatchLoops } from "../hatch/earcutLoops";

/** Result of one stencil tessellation pass. */
export interface ViewportStencilGeometry {
  /** Flat XY positions for the stencil mesh. */
  positionsXY: Float32Array;
  /** Triangle indices. */
  indices: Uint32Array;
  /**
   * Reserved viewportIdx value for the stencil ref. Stable across
   * frames as long as the same viewport entity is in the scene.
   */
  viewportIdx: number;
}

let nextViewportIdx = 1;
const idxAllocations: Map<string, number> = new Map();

/**
 * Reserve (or recall) a stable stencil ref for a viewport entity.
 * Subsequent calls with the same entity id return the same idx so
 * the renderer can amortise stencil-buffer writes across frames.
 *
 * Reset by `resetViewportClipping` on hot reload / scene clear.
 */
export function assignViewportIdx(viewportEntityId: string): number {
  const existing = idxAllocations.get(viewportEntityId);
  if (existing !== undefined) return existing;
  // 8-bit stencil → wrap at 255. We reuse low ids first; per-frame
  // collisions are vanishingly rare (< 20 viewports per layout).
  const idx = nextViewportIdx;
  nextViewportIdx = (nextViewportIdx % 255) + 1;
  idxAllocations.set(viewportEntityId, idx);
  return idx;
}

/** Test/HMR helper — wipe the per-entity stencil-ref allocation map. */
export function resetViewportClipping(): void {
  nextViewportIdx = 1;
  idxAllocations.clear();
}

/**
 * Tessellate the viewport clipping polygon into a stencil mesh. The
 * polygon comes in two shapes:
 *   - Rectangular (4-vertex) — already rotated by the adapter if
 *     `viewport.rotation !== 0`.
 *   - XCLIP polygon (≥ 3 vertices) — caller passes `clipPath` directly.
 *
 * Both feed into the same earcut path, so this function returns a
 * uniform `ViewportStencilGeometry`.
 */
export function tessellateViewportClip(args: {
  viewportEntityId: string;
  rect: Vec3[];
  clipPath?: Vec3[];
}): ViewportStencilGeometry | null {
  const { viewportEntityId, rect, clipPath } = args;
  const polygon =
    clipPath && clipPath.length >= 3
      ? clipPath
      : rect && rect.length >= 3
        ? rect
        : null;
  if (!polygon) return null;

  // Reject 0-area polygons (CLAUDE.md edge case: 0-area clip → skip).
  const area = Math.abs(signedArea2D(polygon));
  if (area < 1e-9) return null;

  const tess = tessellateHatchLoops([
    {
      vertices: polygon.map((p) => ({ x: p.x, y: p.y, z: p.z })),
      isHole: false,
    },
  ]);
  if (tess.indices.length === 0) return null;

  return {
    positionsXY: tess.positionsXY,
    indices: tess.indices,
    viewportIdx: assignViewportIdx(viewportEntityId),
  };
}

/** Polygon signed area in XY. */
function signedArea2D(points: Vec3[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    if (!a || !b) continue;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

/**
 * Configure a Three.js material for stencil-clipped child rendering.
 * Caller passes the material *type* shape (we only set the relevant
 * fields, no THREE imports needed at module load — keeps the file
 * usable in jsdom unit tests).
 */
export interface StencilMaterialFields {
  stencilWrite?: boolean;
  stencilRef?: number;
  stencilFunc?: number;
  stencilFail?: number;
  stencilZFail?: number;
  stencilZPass?: number;
}

/**
 * Set up the stencil-test fields on a child entity's material so the
 * material draws only inside the viewport's stencil region.
 *
 * Constants (numbers vs symbolic) are passed in by the caller —
 * production code uses `THREE.EqualStencilFunc` / `THREE.KeepStencilOp`,
 * tests pass arbitrary numbers and inspect the resulting object.
 */
export function configureChildMaterialForViewport(
  material: StencilMaterialFields,
  args: {
    viewportIdx: number;
    equalFunc: number;
    keepOp: number;
  },
): void {
  material.stencilWrite = true;
  material.stencilRef = args.viewportIdx;
  material.stencilFunc = args.equalFunc;
  material.stencilFail = args.keepOp;
  material.stencilZFail = args.keepOp;
  material.stencilZPass = args.keepOp;
}

/**
 * Set up the stencil-pass material that *writes* `viewportIdx` into
 * the stencil buffer (ALWAYS pass, REPLACE op). This is the inverse of
 * `configureChildMaterialForViewport` — caller draws the stencil mesh
 * with this configured material before rendering children.
 */
export function configureStencilWriteMaterial(
  material: StencilMaterialFields,
  args: {
    viewportIdx: number;
    alwaysFunc: number;
    replaceOp: number;
    keepOp: number;
  },
): void {
  material.stencilWrite = true;
  material.stencilRef = args.viewportIdx;
  material.stencilFunc = args.alwaysFunc;
  material.stencilFail = args.keepOp;
  material.stencilZFail = args.keepOp;
  material.stencilZPass = args.replaceOp;
}

// ---------------------------------------------------------------------------
// THREE-level stencil clip pass (EPIC-VIEWPORT-CLIP slice 3 render arm)
// ---------------------------------------------------------------------------

/**
 * The full set of stencil-buffer material fields THREE.js reads each draw
 * (three.cjs `setMaterial`: `setTest(stencilWrite)` then, when true,
 * `setMask`/`setFunc`/`setOp`). We snapshot/restore EXACTLY these so a
 * material's pre-pass stencil state (e.g. none) is left byte-identical
 * after the viewport pass.
 */
interface MaterialStencilSnapshot {
  material: THREE.Material;
  stencilWrite: boolean;
  stencilRef: number;
  stencilFunc: THREE.StencilFunc;
  stencilFail: THREE.StencilOp;
  stencilZFail: THREE.StencilOp;
  stencilZPass: THREE.StencilOp;
  stencilFuncMask: number;
  stencilWriteMask: number;
}

/** Handle returned by {@link beginViewportClipStencil}; call `restore()` after the model render. */
export interface ViewportClipStencilPass {
  restore(): void;
}

/**
 * Collect every unique `THREE.Material` reachable from `scene` (a material
 * may be shared across many meshes — we configure each only once).
 */
function collectSceneMaterials(scene: THREE.Object3D): Set<THREE.Material> {
  const materials = new Set<THREE.Material>();
  scene.traverse((obj) => {
    const mat = (obj as THREE.Mesh | THREE.Line | THREE.Points).material as
      | THREE.Material
      | THREE.Material[]
      | undefined;
    if (!mat) return;
    if (Array.isArray(mat)) {
      for (const m of mat) materials.add(m);
    } else {
      materials.add(mat);
    }
  });
  return materials;
}

/**
 * THREE.WebGLRenderer's internal `state.buffers.stencil` exposes a cache-synced
 * `setMask(mask)` (the same one THREE.js calls internally — see three.cjs
 * `WebGLRenderer.clear()`: `state.buffers.stencil.setMask(0xffffffff)` before a
 * stencil clear). It is not on the public type surface, so narrow to exactly the
 * shape we use. Going through THREE (rather than a raw `gl.stencilMask`) keeps
 * THREE's cached stencil-write-mask in sync — a raw GL call would desync it and
 * make a later THREE draw skip the (redundant) mask set, re-leaking the bug.
 */
type RendererWithStencilState = {
  state?: { buffers?: { stencil?: { setMask?: (mask: number) => void } } };
};

/**
 * Set the GL stencil WRITE mask to all bits via THREE's cache-synced setter so a
 * subsequent stencil clear scrubs every bit (and fresh writes land). Mirrors
 * THREE's own clear() convention. No-op if the (private) state path is absent
 * (e.g. a stub renderer in tests) — the caller's behaviour degrades to the
 * pre-fix path, which the unit test asserts against the real setter. (Codex #326.)
 */
function setStencilWriteMaskAllBits(gl: THREE.WebGLRenderer): void {
  const stencil = (gl as unknown as RendererWithStencilState).state?.buffers
    ?.stencil;
  stencil?.setMask?.(0xffffffff);
}

/**
 * Resolve the WCS clip polygon for a viewport into renderer (offset-world)
 * coordinates. `clipPath` is stored in WCS/model coordinates (the SPATIAL_FILTER
 * boundary transformed by the inverse-insert matrix — see
 * `drawing-model/viewportClipFinalize.ts`). The model-space meshes are drawn
 * with `applyOffset` subtracted (coordinateOrigin), and the per-VP model camera
 * (`buildViewportModelCamera`) renders them in that same offset-world frame, so
 * the stencil polygon MUST go through the SAME `applyOffset` to land exactly on
 * top of the geometry it clips. Returns null when fewer than 3 finite vertices.
 */
export function resolveViewportClipPolygon(
  clipPath: ReadonlyArray<{ x: number; y: number; z?: number }> | undefined,
): Vec3[] | null {
  if (!clipPath || clipPath.length < 3) return null;
  const out: Vec3[] = [];
  for (const p of clipPath) {
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    out.push(applyOffset({ x: p.x, y: p.y, z: Number.isFinite(p.z) ? (p.z as number) : 0 }));
  }
  return out.length >= 3 ? out : null;
}

/**
 * Begin a stencil-clip pass for ONE viewport whose model content is about to be
 * rendered through `vpCamera`.
 *
 * Wiring (the render arm that activates the otherwise-unused stencil helpers):
 *   1. Tessellate the offset-world clip polygon (earcut) and reserve a stable
 *      `viewportIdx`.
 *   2. Clear the stencil buffer to 0 inside the active scissor rect, then draw
 *      the polygon mesh with a stencil-WRITE material (ALWAYS pass, REPLACE op,
 *      colorWrite/depthWrite/depthTest off) so it marks the pentagon pixels with
 *      `viewportIdx` WITHOUT touching color/depth.
 *   3. Configure every unique scene material with a stencil EQUAL test against
 *      `viewportIdx` (snapshotting prior stencil fields). THREE re-applies these
 *      per material on the upcoming `gl.render(modelScene, vpCamera)`, so only
 *      fragments inside the polygon survive — the slanted pentagon edge clips
 *      the right-hand geometry that sits inside the VP rect but outside the clip.
 *
 * The caller renders the model scene AFTER this returns, then calls
 * `restore()` (in a finally) which reverts every material's stencil fields and
 * disposes the stencil mesh. Returns null when the viewport has no resolvable
 * (≥3-vertex, non-zero-area) clip polygon — the caller then renders unclipped
 * (rectangular scissor only), matching AutoCAD's full-window behaviour.
 *
 * @param gl       the live WebGLRenderer (its context's stencil buffer is used)
 * @param scene    the model-space scene whose meshes will be stencil-tested
 * @param vpCamera the per-viewport model-space camera (same frame as the meshes)
 * @param args     viewport entity id (for a stable idx) + the WCS clip polygon
 */
export function beginViewportClipStencil(
  gl: THREE.WebGLRenderer,
  scene: THREE.Scene,
  vpCamera: THREE.Camera,
  args: {
    viewportEntityId: string;
    clipPath: ReadonlyArray<{ x: number; y: number; z?: number }> | undefined;
    /**
     * Optional model-space background colour (0..1 RGB). When supplied, the clip
     * pass paints the polygon INTERIOR with this colour (under the stencil EQUAL
     * test, depth-write off) before the caller renders model geometry, so the
     * area OUTSIDE the polygon keeps the paper sheet rather than a dark slab
     * (Codex #326 P2). Omit for callers that handle their own background.
     */
    backgroundColor?: { r: number; g: number; b: number };
  },
): ViewportClipStencilPass | null {
  const polygon = resolveViewportClipPolygon(args.clipPath);
  if (!polygon) return null;

  const tess = tessellateViewportClip({
    viewportEntityId: args.viewportEntityId,
    rect: polygon, // polygon is ≥3 verts so it is used directly as the clip
    clipPath: polygon,
  });
  if (!tess) return null;

  const { viewportIdx } = tess;

  // ── Build the stencil-write mesh (XY positions, Z=0 — the clip plane). ──
  const geom = new THREE.BufferGeometry();
  const vertCount = tess.positionsXY.length / 2;
  const positions = new Float32Array(vertCount * 3);
  for (let i = 0; i < vertCount; i++) {
    positions[i * 3] = tess.positionsXY[i * 2]!;
    positions[i * 3 + 1] = tess.positionsXY[i * 2 + 1]!;
    positions[i * 3 + 2] = 0;
  }
  geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geom.setIndex(new THREE.BufferAttribute(tess.indices, 1));

  const stencilMat = new THREE.MeshBasicMaterial();
  // The stencil mesh writes ONLY the stencil buffer — never color or depth.
  stencilMat.colorWrite = false;
  stencilMat.depthWrite = false;
  stencilMat.depthTest = false;
  // DOUBLE-sided so the stencil triangles write `viewportIdx` regardless of the
  // clip boundary's winding order (Codex #326 P2). earcut preserves the input
  // winding; a valid DXF VPCLIP boundary may arrive CW (e.g. a 340 LWPOLYLINE
  // authored clockwise, or a SPATIAL_FILTER boundary whose inverse-insert matrix
  // mirrors the polygon). With the default FrontSide culling a CW-wound stencil
  // mesh would be fully back-face culled → nothing marks the buffer → every child
  // material's EQUAL test fails → the clipped viewport loses ALL its model
  // content. DoubleSide makes winding irrelevant for this pure stencil-write pass.
  stencilMat.side = THREE.DoubleSide;
  configureStencilWriteMaterial(stencilMat, {
    viewportIdx,
    alwaysFunc: THREE.AlwaysStencilFunc,
    replaceOp: THREE.ReplaceStencilOp,
    keepOp: THREE.KeepStencilOp,
  });
  const stencilMesh = new THREE.Mesh(geom, stencilMat);
  stencilMesh.frustumCulled = false;

  const ctx = gl.getContext() as WebGLRenderingContext;
  // Clear the stencil buffer (inside the currently-active scissor rect) so the
  // previous VP's marks never leak into this pass. ClearStencil(0) + a scissored
  // clear keeps the cost bounded to the VP window.
  //
  // CRITICAL (Codex #326 P2): a raw `ctx.clear(STENCIL_BUFFER_BIT)` honours the
  // CURRENT GL stencil WRITE mask. The child materials below set
  // `stencilWriteMask = 0x00`, which makes THREE call `gl.stencilMask(0)` during
  // the model render — and THREE caches that masked-off state. So a SUBSEQUENT
  // entry here (the SAME viewport id after a pan/zoom or clip edit, or the next
  // VP) would hit this clear with the write mask still at 0 → the clear writes
  // NOTHING → stale stencil pixels survive and pass the later EQUAL test, leaking
  // geometry through the clip. Mirror THREE's own `WebGLRenderer.clear()`
  // (`state.buffers.stencil.setMask(0xffffffff)` before clearing stencil) so the
  // clear actually scrubs all bits AND THREE's cached mask stays in sync (a raw
  // `ctx.stencilMask` would desync THREE's WebGLState cache). Reset back to the
  // all-bits default in `restore()` so the next pass starts clean.
  setStencilWriteMaskAllBits(gl);
  ctx.clearStencil(0);
  ctx.clear(ctx.STENCIL_BUFFER_BIT);

  // Draw the polygon → stencil buffer now marks viewportIdx inside the pentagon.
  gl.render(stencilMesh, vpCamera);

  // ── Background fill (Codex #326 P2) ──────────────────────────────────────
  // Paint the polygon INTERIOR with the dark model background BEFORE the model
  // geometry, EQUAL-testing the just-written stencil so ONLY inside-polygon
  // pixels are filled — the area between the polygon and the rectangular VP frame
  // keeps the paper sheet (the caller skipped the whole-rect colour clear for a
  // clipped viewport). Writes colour only (depth-write/test off, stencil
  // KEEP/no-write) so it neither perturbs the stencil marks nor depth-occludes
  // the model render that follows.
  let bgMesh: THREE.Mesh | null = null;
  let bgMat: THREE.MeshBasicMaterial | null = null;
  if (args.backgroundColor) {
    bgMat = new THREE.MeshBasicMaterial();
    // The rectangular-VP background path uses a RAW `gl.clearColor(r,g,b,1)`
    // (viewportRenderPass.ts) which writes the value to the framebuffer verbatim
    // (≈ r*255). A THREE material colour, by contrast, is colour-managed: with
    // ColorManagement on (three r152+ default) `setRGB` treats the inputs as
    // WORKING-LINEAR and encodes linear→sRGB on output, so linear 0.122 would
    // emit sRGB ≈ 0.39 (≈99/255) — a washed-out grey that does NOT match the
    // rectangular VP's dark #1f1f23 (≈31). Declare the inputs as SRGBColorSpace
    // so THREE round-trips them (sRGB→linear store, linear→sRGB output) back to
    // the verbatim 0.122 — the clipped-VP interior fill then matches the
    // rectangular-VP clearColor exactly (Codex #326 r3). (Codify the canonical
    // input space rather than rely on the working-space default.)
    bgMat.color.setRGB(
      args.backgroundColor.r,
      args.backgroundColor.g,
      args.backgroundColor.b,
      THREE.SRGBColorSpace,
    );
    bgMat.colorWrite = true;
    bgMat.depthWrite = false;
    bgMat.depthTest = false;
    bgMat.side = THREE.DoubleSide;
    // EQUAL-test the stencil (inside polygon only); never WRITE the stencil.
    configureChildMaterialForViewport(bgMat, {
      viewportIdx,
      equalFunc: THREE.EqualStencilFunc,
      keepOp: THREE.KeepStencilOp,
    });
    bgMat.stencilWriteMask = 0x00;
    bgMat.stencilFuncMask = 0xff;
    bgMesh = new THREE.Mesh(geom, bgMat);
    bgMesh.frustumCulled = false;
    gl.render(bgMesh, vpCamera);
  }

  // ── Configure every scene material to EQUAL-test against viewportIdx. ──
  const materials = collectSceneMaterials(scene);
  const snapshots: MaterialStencilSnapshot[] = [];
  for (const material of materials) {
    snapshots.push({
      material,
      stencilWrite: material.stencilWrite,
      stencilRef: material.stencilRef,
      stencilFunc: material.stencilFunc,
      stencilFail: material.stencilFail,
      stencilZFail: material.stencilZFail,
      stencilZPass: material.stencilZPass,
      stencilFuncMask: material.stencilFuncMask,
      stencilWriteMask: material.stencilWriteMask,
    });
    configureChildMaterialForViewport(material, {
      viewportIdx,
      equalFunc: THREE.EqualStencilFunc,
      keepOp: THREE.KeepStencilOp,
    });
    // Children only TEST the stencil — they must never WRITE it (KEEP all ops,
    // mask 0) or one child would overwrite the pentagon marks for the next.
    // (Stencil fields are GL state applied per-draw, NOT shader uniforms, so no
    // material.needsUpdate / recompile is required.)
    material.stencilWriteMask = 0x00;
    material.stencilFuncMask = 0xff;
  }

  return {
    restore(): void {
      for (const s of snapshots) {
        const m = s.material;
        m.stencilWrite = s.stencilWrite;
        m.stencilRef = s.stencilRef;
        m.stencilFunc = s.stencilFunc;
        m.stencilFail = s.stencilFail;
        m.stencilZFail = s.stencilZFail;
        m.stencilZPass = s.stencilZPass;
        m.stencilFuncMask = s.stencilFuncMask;
        m.stencilWriteMask = s.stencilWriteMask;
      }
      // Reset the GL stencil WRITE mask to the all-bits default (Codex #326 P2).
      // The model render above left THREE's cached stencil write mask at 0 (the
      // child materials' `stencilWriteMask = 0x00`). Restoring material fields
      // does NOT touch the live GL mask, so without this the next clear/pass
      // would inherit a masked-off (0) stencil and either fail to clear stale
      // marks or fail to write fresh ones. Mirror THREE's clear() convention so
      // the next VP / frame begins with a fully-writable stencil buffer.
      setStencilWriteMaskAllBits(gl);
      geom.dispose();
      stencilMat.dispose();
      // The background-fill material (if any) shares `geom` (disposed above) — it
      // only needs its own material disposed (Codex #326 P2).
      bgMat?.dispose();
    },
  };
}
